import axios from '@api';

import {
  extractChatFlowState,
  transformChatSessionData,
  transformProject,
} from './utilities/helpers';
import chatApi from './api';

const buildTopicIdsUpdatePayload = ({ flowId, topicIds = [] }) => {
  const data = { topic_ids: topicIds };

  if (flowId !== undefined && flowId !== null) {
    data.flow_id = flowId;
  }

  return data;
};

const buildFlowStateUpdatePayload = ({ flowState = {} }) => {
  const normalizedFlowState = flowState && typeof flowState === 'object' ? flowState : {};
  const data = {};

  if (normalizedFlowState.flowId !== undefined && normalizedFlowState.flowId !== null) {
    data.flow_id = normalizedFlowState.flowId;
  }

  if (Object.prototype.hasOwnProperty.call(normalizedFlowState, 'businessQuestion')) {
    data.business_question = normalizedFlowState?.businessQuestion || null;
  }

  if (Object.prototype.hasOwnProperty.call(normalizedFlowState, 'editablePromptAnswers')) {
    data.editable_prompt_answers = normalizedFlowState?.editablePromptAnswers || {};
  }

  if (Object.prototype.hasOwnProperty.call(normalizedFlowState, 'narrativeName')) {
    data.narrative_name = normalizedFlowState?.narrativeName || '';
  }

  if (Array.isArray(normalizedFlowState?.components)) {
    data.topic_ids = normalizedFlowState.components;
  }

  return data;
};

const preserveKnownFlowId = (flowState, flowId) => {
  if (flowState == null || typeof flowState !== 'object') return flowState;
  if (flowState.flowId != null || flowId == null) return flowState;

  return {
    ...flowState,
    flowId,
  };
};

/**
 * Create a new chat session for the user on the chat service.
 * Chat session is authenticated and created for the user
 * based on the JWT header with the user ID in its claims.
 * Responds with the chat session UUID that was created,
 * which will be used for adding messages to and reading the chat history.
 * @return {Promise<string>} chatSessionId
 */
export const createChatSession = () => {
  return chatApi({
    data: {},
    method: 'post',
    url: '/api/v1/chat',
  }).then(({ data }) => {
    const sessionId = data?.chatSessionId;

    if (!sessionId) {
      throw new Error('Chat service did not include a session identifier in its response.');
    }
    return sessionId;
  });
};

/**
 * Sends a chat message to the chat service.
 * @param {Object} options
 * @param {string} options.chatSessionId
 * @param {string} options.userInput
 * @return {Promise<any>}
 */
export const createChatMessage = ({ chatSessionId, userInput }) => {
  return chatApi({
    data: userInput,
    method: 'post',
    url: `/api/v1/chat/${chatSessionId}`,
  }).then(({ data }) => data);
};

/**
 * Fetches a chat session from the chat service and transforms it for UI.
 * @param {Object} options
 * @param {string} options.chatSessionId
 * @param {(role: string) => string} [options.createFallbackId]
 * @param {AbortSignal} [options.signal]
 * @return {Promise<{flowId: number|null, flowState: Object|null, messages: Array<{id: string, role: string, content: string, isStreaming?: boolean}>}>}
 */
export const readChatSession = ({ chatSessionId, createFallbackId, signal }) => {
  return chatApi({
    method: 'get',
    signal,
    url: `/api/v1/chat/${chatSessionId}`,
  }).then(({ data }) => transformChatSessionData(data, { createFallbackId }));
};

/**
 * Creates a project tied to a chat session.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId chat session identifier
 * @param {Array<number>} [options.flowIds=[]] flows to associate on create
 * @param {string} [options.name] project name
 * @returns {Promise<ReturnType<typeof transformProject>>}
 */
export const createProject = ({ chatSessionId, flowIds = [], name }) => {
  return axios({
    data: {
      chat_session_id: chatSessionId,
      flow_ids: flowIds,
      name,
    },
    method: 'post',
    url: '/v2/api/projects',
    withCredentials: true,
  }).then(({ data }) => transformProject(data));
};

/**
 * Fetches a project by id.
 *
 * @param {Object} options
 * @param {number|string} options.projectId project identifier
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<ReturnType<typeof transformProject>>}
 */
export const readProject = ({ projectId, signal }) => {
  return axios({
    method: 'get',
    signal,
    url: `/v2/api/projects/${projectId}`,
    withCredentials: true,
  }).then(({ data }) => transformProject(data));
};

/**
 * Fetches narratives (flows) for a project chat session.
 *
 * @param {Object} options
 * @param {number|string} options.chatSessionId project chat session identifier
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array<Object>>}
 */
export const readProjectFlows = ({ chatSessionId, signal }) => {
  return axios({
    method: 'get',
    signal,
    url: `/v2/api/projects/${chatSessionId}/flows`,
    withCredentials: true,
  }).then(({ data }) => (Array.isArray(data) ? data : []));
};

/**
 * Adds a flow to an existing project.
 *
 * @param {Object} options
 * @param {number|string} options.flowId flow identifier
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const addFlowToProject = ({ flowId, projectId }) => {
  return axios({
    method: 'put',
    url: `/v2/api/projects/${projectId}/add/${flowId}`,
    withCredentials: true,
  });
};

/**
 * Removes a flow from a project.
 *
 * @param {Object} options
 * @param {number|string} options.flowId flow identifier
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const removeFlowFromProject = ({ flowId, projectId }) => {
  return axios({
    method: 'put',
    url: `/v2/api/projects/${projectId}/remove/${flowId}`,
    withCredentials: true,
  });
};

/**
 * Updates the topic/component order stored in the chat flow state.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId chat session identifier
 * @param {number|string|null} [options.flowId] active flow identifier to preserve
 * @param {Array<string|number>} options.topicIds ordered list of topic/component identifiers
 * @returns {Promise<Object|null>} updated flow state, if provided by the API
 */
export const updateFlowStateTopicIds = ({ chatSessionId, flowId = null, topicIds = [] }) => {
  if (!chatSessionId) {
    return Promise.reject(new Error('chatSessionId is required to update the flow state.'));
  }

  return chatApi({
    data: buildTopicIdsUpdatePayload({ flowId, topicIds }),
    method: 'put',
    url: `/api/v1/flow-state/${chatSessionId}`,
  }).then(({ data }) => {
    if (!data) return null;
    return preserveKnownFlowId(
      extractChatFlowState(
        data?.flow_state || data?.flowState || null,
        data?.required_prompts || data?.requiredPrompts || null
      ),
      flowId
    );
  });
};

/**
 * Updates draft flow-state fields stored for a chat session.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId chat session identifier
 * @param {Object} options.flowState partial flow-state payload to persist
 * @returns {Promise<Object|null>} updated flow state, if provided by the API
 */
export const updateFlowState = ({ chatSessionId, flowState = {} }) => {
  if (!chatSessionId) {
    return Promise.reject(new Error('chatSessionId is required to update the flow state.'));
  }

  return chatApi({
    data: buildFlowStateUpdatePayload({ flowState }),
    method: 'put',
    url: `/api/v1/flow-state/${chatSessionId}`,
  }).then(({ data }) => {
    if (!data) return null;
    return preserveKnownFlowId(
      extractChatFlowState(
        data?.flow_state || data?.flowState || null,
        data?.required_prompts || data?.requiredPrompts || null
      ),
      flowState?.flowId
    );
  });
};

/**
 * Get project list for Narratives Project History Window
 * @return {Promise}
 */
export const readProjectList = ({ signal }) => {
  return axios({
    method: 'get',
    signal,
    url: '/v2/api/projects',
    withCredentials: true,
  }).then(({ data }) => {
    const items = data?.items || [];
    return {
      ...data,
      items: items.map((project) => transformProject(project)),
    };
  });
};

/**
 * Updates the display name of a project.
 *
 * @param {Object} options
 * @param {string} options.name new project name
 * @param {number|string} options.chatSessionId chat session identifier
 * @returns {Promise<ReturnType<typeof transformProject>>}
 */
export const renameProject = ({ chatSessionId, name }) => {
  return axios({
    data: { name },
    method: 'put',
    url: `/v2/api/projects/${chatSessionId}/rename`,
    withCredentials: true,
  }).then(({ data }) => transformProject({ ...data, chat_session_id: chatSessionId, name }));
};

/**
 * Soft deletes a project.
 *
 * @param {Object} options
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const deleteProject = ({ projectId }) => {
  return axios({
    method: 'delete',
    url: `/v2/api/projects/${projectId}/delete`,
    withCredentials: true,
  });
};

/**
 * Update favorite status for a project in Narratives Project History Window
 * @param {Object} options
 * @param {String} options.chatSessionId
 * @param {Boolean} options.isFavorite
 * @return {Promise<ReturnType<typeof transformProject>>}
 */
export const favoriteProject = ({ chatSessionId, isFavorite }) => {
  return axios({
    data: { is_favorite: isFavorite },
    method: 'post',
    url: `/v2/api/projects/${chatSessionId}/favorite`,
    withCredentials: true,
  }).then(({ data }) => transformProject({ ...data, chat_session_id: chatSessionId }));
};
